import {LocaleLinesSync} from '@ui/i18n/locale-lines-store';
import {InPageAnchorGuard} from '@common/ui/navigation/section-nav/in-page-anchor-guard';
import {SiteConfig} from '@app/site-config';
import {SettingsPreviewListener} from '@common/admin/settings/preview/settings-preview-listener';
import {auth, useAuth} from '@common/auth/use-auth';
import {ColorSchemeProvider} from '@common/core/color-scheme-provider';
import {IconThemeProvider} from '@common/uploads/components/file-type-icon/icon-theme';
import {BaseSiteConfig} from '@common/core/settings/base-site-config';
import {SiteConfigContext} from '@common/core/settings/site-config-context';
import {useShowGlobalLoadingBar} from '@common/core/use-show-global-loading-bar';
import {
  isChunkLoadError,
  reloadOnceAfterChunkError,
} from '@common/core/retry-lazy-import';
import {PageErrorMessage} from '@common/http/errors/page-error-message';
import {apiErrorStatusIs} from '@common/http/errors/parsed-api-error';
import {queryClient} from '@common/http/query-client';
import {RealtimeNotifications} from '@common/notifications/realtime-notifications';
import {CommandPalette} from '@common/ui/command-palette/command-palette';
import {CookieNotice} from '@common/ui/cookie-notice/cookie-notice';
import {ImpersonationBanner} from '@common/auth/impersonation-banner';
import {
  mustVerifyEmailFirst,
  verifyEmailUrl,
} from '@common/auth/email-verification-state';
import {EmailVerificationBanner} from '@common/auth/email-verification-banner';
import {GoogleOneTap} from '@common/auth/one-tap/google-one-tap';
import {ConnectionStatusBar} from '@common/ui/connection-status-bar';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';
import {Toaster} from '@shadcn/toast/toast';
import {Tooltip} from '@shadcn/tooltip/tooltip';
import {QueryClientProvider} from '@tanstack/react-query';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {DialogStoreOutlet} from '@ui/overlays/store/dialog-store-outlet';
import {TopProgressBar} from '@ui/progress/top-progress-bar';
import {ToastContainer} from '@ui/toast/toast-container';
import deepMerge from 'deepmerge';
import {domAnimation, LazyMotion} from 'framer-motion';
import {NuqsAdapter} from 'nuqs/adapters/react-router/v7';
import {Fragment, useEffect, useState} from 'react';
import {
  createBrowserRouter,
  MiddlewareFunction,
  Navigate,
  Outlet,
  redirect,
  RouterProvider,
  ScrollRestoration,
  useNavigation,
  useRouteError,
} from 'react-router';

const mergedConfig = deepMerge(BaseSiteConfig, SiteConfig);

type Router = ReturnType<typeof createBrowserRouter>;

interface Props {
  router: Router;
}
export function CommonProvider({router}: Props) {
  return (
    <QueryClientProvider client={queryClient}>
      <LazyMotion features={domAnimation}>
        <SiteConfigContext.Provider value={mergedConfig}>
          <ColorSchemeProvider>
            <IconThemeProvider>
              <Tooltip.Provider>
                <NuqsAdapter>
                  <RouterProvider router={router} useTransitions />
                </NuqsAdapter>
              </Tooltip.Provider>
            </IconThemeProvider>
          </ColorSchemeProvider>
        </SiteConfigContext.Provider>
      </LazyMotion>
    </QueryClientProvider>
  );
}

export function RootRoute() {
  return (
    <Fragment>
      <RealtimeNotifications />
      <ImpersonationBanner />
      <EmailVerificationBanner />
      <ConnectionStatusBar />
      <GlobalTopLoadingBar />
      <Outlet />
      <CommandPalette />
      <SettingsPreviewListener />
      <CookieNotice />
      <GoogleOneTap />
      <LocaleLinesSync />
      <InPageAnchorGuard />
      <ToastContainer />
      <Toaster />
      <DialogStoreOutlet />
      <ScrollRestoration />
    </Fragment>
  );
}

export function RootErrorElement() {
  const [bar] = useState(() => new TopProgressBar());
  const {isLoggedIn} = useAuth();
  const error = useRouteError();

  // hide loading bar on error page
  useEffect(() => {
    bar.hide();
  }, [bar]);

  if (error) {
    console.error('[drive] Route failed', error);
  }

  if (isChunkLoadError(error)) {
    reloadOnceAfterChunkError();
  }

  if (apiErrorStatusIs(error, 404)) {
    return <NotFoundPage />;
  }

  const viewingAs = Boolean(getBootstrapData()?.impersonator);
  if (
    (apiErrorStatusIs(error, 401) || apiErrorStatusIs(error, 403)) &&
    !isLoggedIn &&
    !viewingAs
  ) {
    const loc = window.location;
    const redirectTo = encodeURIComponent(`${loc.pathname}${loc.search}`);
    return <Navigate to={`/login?redirect=${redirectTo}`} replace />;
  }

  return (
    <>
      <ImpersonationBanner />
      <PageErrorMessage
        error={
          viewingAs && apiErrorStatusIs(error, 401)
            ? new Error('This account page could not load while viewing as another person. Return to admin, then open General again.')
            : error
        }
      />
    </>
  );
}

export {mustVerifyEmailFirst} from '@common/auth/email-verification-state';

export const rootRouteMiddleware: MiddlewareFunction[] = [
  ({request}) => {
    const pathname = new URL(request.url).pathname;
    if (mustVerifyEmailFirst(pathname)) {
      const url = new URL(request.url);
      throw redirect(verifyEmailUrl(`${url.pathname}${url.search}`));
    }

    if (
      !/\/suspended\/?$/.test(pathname) &&
      auth.user?.banned_at &&
      !getBootstrapData().impersonator
    ) {
      throw redirect('/suspended');
    }
  },
];

export function GlobalTopLoadingBar() {
  const {state} = useNavigation();

  // only start showing loader after 50ms, this will prevent it from showing on most js chunk fetches
  useShowGlobalLoadingBar({isLoading: state === 'loading', delay: 80});

  return null;
}
